import React from 'react'
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form'
import * as yup from 'yup'

import styled from '../../constants/theme'
import { FormErrors } from '../../hooks/useForm'
import PillButton from '../elements/PillButton'
import DateField from '../fields/DateField'
import RadioField from '../fields/RadioField'
import SelectField from '../fields/SelectField'
import TextField from '../fields/TextField'
import AutoSave from './AutoSave'
import { allowedInputValue, formattedValue, storedValue } from './InputElement'
import SwitchElement from './Switch'
import CheckboxElement from './Checkbox'

export const useYupValidationResolver = (validationSchema) =>
  React.useCallback(
    async (data) => {
      try {
        const values = await validationSchema.validate(data, {
          abortEarly: false,
        })

        return {
          values,
          errors: {},
        }
      } catch (errors) {
        return {
          values: {},
          errors: errors.inner.reduce(
            (allErrors, currentError) => ({
              ...allErrors,
              [currentError.path]: {
                type: currentError.type ?? 'validation',
                message: currentError.message,
              },
            }),
            {},
          ),
        }
      }
    },
    [validationSchema],
  )

interface Props {
  additionalButtons?: React.ReactNode[]
  autoSave?: boolean
  children: React.ReactNode
  className?: string
  defaultValues: any
  onSubmit: (formValues: any, e?: any) => void
  serverErrors?: FormErrors | null
  shouldDisableSubmit?: boolean
  shouldEnableWhenDirty?: boolean
  shouldResetOnSubmit?: boolean
  submitButtonText?: string
  validationSchema: yup.AnyObject
}

export const Form: React.FC<Props> = ({
  additionalButtons,
  autoSave,
  children,
  className,
  defaultValues,
  onSubmit,
  serverErrors,
  shouldDisableSubmit,
  shouldEnableWhenDirty,
  shouldResetOnSubmit,
  submitButtonText,
  validationSchema,
}) => {
  const methods = useForm({
    defaultValues,
    resolver: useYupValidationResolver(validationSchema),
  })
  const formName = React.useRef(Date.now().valueOf())

  React.useEffect(() => {
    if (!shouldResetOnSubmit) {
      return
    }

    const { formState, reset } = methods
    formState.isSubmitSuccessful && reset({ ...defaultValues })
  }, [defaultValues, methods, shouldResetOnSubmit])

  React.useEffect(() => {
    if (!serverErrors) {
      return
    }

    const defaultValueKeys = Object.keys(defaultValues)
    Object.keys(serverErrors).forEach((k) => {
      if (defaultValueKeys.includes(k)) {
        const message = serverErrors[k]
        methods.setError(k, { type: 'server', message })
      }
    })
  }, [defaultValues, serverErrors, methods])

  return (
    <FormProvider {...methods}>
      <form
        name={`cmd-form-${formName.current}`}
        className={className}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        {children}
        <FormSubmitWrap className="FormSubmit">
          {additionalButtons?.map((btn, i) => (
            <React.Fragment key={i}>{btn}</React.Fragment>
          ))}

          {autoSave ? (
            <AutoSave onSubmit={onSubmit} defaultValues={defaultValues} />
          ) : (
            <FormSubmit
              buttonText={submitButtonText}
              shouldEnableWhenDirty={shouldEnableWhenDirty}
              shouldDisable={shouldDisableSubmit}
            />
          )}
        </FormSubmitWrap>
      </form>
    </FormProvider>
  )
}

export enum ElementType {
  CHECKBOX = 'checkbox',
  DATE = 'date',
  RADIO = 'radio',
  SELECT = 'select',
  SWITCH = 'switch',
  TEL = 'tel',
  TEXT = 'text',
}

export interface SelectOption {
  label: string
  value: any
}

export interface Field {
  className?: string
  helperText?: string
  isInline?: boolean
  label: string
  max?: string
  min?: string
  multiline?: boolean
  name: string
  onBlur?: React.FocusEventHandler<HTMLElement>
  options?: SelectOption[]
  placeholder?: string
  type: ElementType
}

export const FormFieldWrapper: React.FC<Field> = (props) => {
  const { name, type } = props
  const ctx = useFormContext()
  const {
    control,
    formState: { errors, isSubmitted },
    trigger,
  } = ctx

  const errMessage = errors?.[name]?.message
  const theError = errMessage ? errMessage.toString() : undefined

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        let component: React.ReactNode

        switch (type) {
          case ElementType.CHECKBOX:
            component = (
              <CheckboxElement
                id={name}
                {...props}
                onChange={(changeObj) => {
                  const isChecked = changeObj?.value === 'true'
                  isSubmitted && trigger()
                  onChange(isChecked)
                }}
                value={value ? 'true' : 'false'}
              />
            )
            break
          case ElementType.DATE:
            component = (
              <DateField
                id={name}
                clientErrorText={theError}
                {...props}
                onChange={(e) => {
                  isSubmitted && trigger()
                  onChange(e.target.value)
                }}
                value={value ?? ''}
              />
            )
            break
          case ElementType.RADIO:
            component = (
              <RadioField
                clientErrorText={theError}
                {...props}
                onChange={(e) => {
                  isSubmitted && trigger()
                  onChange(e.target.value)
                }}
                value={value ?? ''}
              />
            )
            break
          case ElementType.SELECT:
            component = (
              <SelectField
                {...props}
                clientErrorText={theError}
                id={name}
                onChange={(e) => {
                  isSubmitted && trigger()
                  onChange(e)
                }}
                options={props.options!}
                value={value ?? ''}
              />
            )
            break
          case ElementType.SWITCH:
            component = (
              <SwitchElement
                {...props}
                onChange={(changeObj) => {
                  const isChecked = changeObj?.value === 'true'
                  isSubmitted && trigger()
                  onChange(isChecked)
                }}
                value={value ? 'true' : 'false'} // the switch deals with strings instead of bools. Pass strings in for representative values
              />
            )
            break
          default:
            const inputType = type === ElementType.TEL ? 'tel' : 'text'
            component = (
              <TextField
                clientErrorText={theError}
                {...props}
                id={name}
                onBlur={() => {
                  // Only run when on a tel input to format the value. This will cause autosave to retrigger when this runs.
                  type === ElementType.TEL &&
                    onChange(formattedValue(type, storedValue(type, value)))
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const theValue = allowedInputValue(type, e.target.value)
                  isSubmitted && trigger()
                  onChange(theValue)
                }}
                type={inputType}
                value={value ?? ''}
              />
            )
            break
        }

        const className = ['FormFieldWrapper', props.className || '']
          .join(' ')
          .trim()
        return <div className={className} children={component} />
      }}
    />
  )
}

interface FormSubmitProps {
  buttonText?: string
  shouldDisable?: boolean
  shouldEnableWhenDirty?: boolean
}
export const FormSubmit: React.FC<FormSubmitProps> = ({
  buttonText,
  shouldDisable,
  shouldEnableWhenDirty,
}) => {
  const {
    formState: { errors, isDirty },
  } = useFormContext()

  return (
    <PillButton
      disabled={
        Object.keys(errors)?.length > 0 ||
        (!isDirty && !shouldEnableWhenDirty) ||
        shouldDisable
      }
      type="submit"
      variant="contained"
    >
      {buttonText || 'Save'}
    </PillButton>
  )
}
export const FormSubmitWrap = styled.div({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 10,
  '& button + button': {
    marginLeft: 8,
  },
})

export default Form
